import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const appName = config.app_name || "Mailwave";
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;
  if (!config.features?.public_registration) return <Navigate to="/login" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      navigate("/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x.msg || JSON.stringify(x)).join("; ")
        : d || err?.message || "Registration failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white dark:bg-neutral-950">
      <div className="hidden lg:flex relative bg-neutral-900 items-center justify-center p-12 text-white">
        <div className="absolute top-8 left-8 flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm flex items-center justify-center bg-white dark:bg-neutral-950">
            <Sparkles className="h-4 w-4" style={{ color: "var(--brand-primary)" }} />
          </div>
          <span className="font-display font-black tracking-tight text-lg">{appName}</span>
        </div>
        <div className="max-w-md">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">Start free</p>
          <h2 className="font-display font-black tracking-tighter text-4xl leading-tight mb-6">
            Build an audience.<br />Send what matters.
          </h2>
          <ul className="space-y-3 text-sm text-neutral-300">
            <li>— Unlimited audiences and segments</li>
            <li>— AI-powered subject lines and content</li>
            <li>— Real-time campaign analytics</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <p className="label-overline mb-3">Create account</p>
          <h1 className="font-display font-black tracking-tighter text-4xl mb-8">Get started.</h1>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Full name</Label>
              <Input required value={form.name} onChange={set("name")} className="mt-2 rounded-sm h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} className="mt-2 rounded-sm h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Password</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={set("password")} className="mt-2 rounded-sm h-11" data-testid="register-password-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Company (optional)</Label>
              <Input value={form.company} onChange={set("company")} className="mt-2 rounded-sm h-11" data-testid="register-company-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm btn-brand font-semibold" data-testid="register-submit-button">
              {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-neutral-900 dark:text-neutral-50 underline underline-offset-4" data-testid="goto-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
